import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { AzureUserInfo } from '@/auth/azure/azureManager'
import DocumentList from '@/components/document/DocumentList'
import AttachmentsManager from '@/components/file-manager/AttachmentsManager'
import { ticket } from '@/model/qsadminapi/QsAdminApiModuleModel'
import TicketEditorCompleteForm from '@/routes/tickets/TicketEditor.complete'
import TicketEditorNoteForm from '@/routes/tickets/TicketEditor.note'
import NoteView from '@/routes/tickets/notes/NoteView'
import {
  canEditTicketNote,
  getTicketNoteQueryKey,
  TicketNoteViewModel,
  upsertTicketNote,
} from '@/routes/tickets/notes/ticketNotes'
import { createTicketAttachmentFolder, getTicketAttachmentPermissions } from '@/routes/tickets/ticketWrite'
import { TicketActionDescriptions, TicketActions } from '@/routes/tickets/TicketEditor.types'
import { useScreenSize } from '@/themes/media-query'
import { Popup } from 'devextreme-react'
import { FileManagerRef } from 'devextreme-react/cjs/file-manager'
import { PopupRef } from 'devextreme-react/cjs/popup'
import React, { useCallback, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import notify from 'devextreme/ui/notify'
import { getTicketDetailQueryKey } from '@/routes/tickets/ticketDetail'
import { writeTicketEditorDraft } from '@/routes/tickets/ticketEditorDraftStorage'

const TicketEditorPopup = (props: {
  refPopup: React.RefObject<PopupRef>
  popup: {
    visible: boolean
    action: TicketActions
    ticket?: ticket | null
    complete?: {
      ticket: ticket | null | undefined
      closure: string
      solution: string
    }
    note?: TicketNoteViewModel
  }
  userInfo: AzureUserInfo | undefined
  onRefreshTicket?: () => void | Promise<void>
}) => {
  const currentScreenSize = useScreenSize()
  const { refPopup, popup, userInfo } = props
  const documentListRef = useRef<any>(null)
  const fileManagerRef = useRef<FileManagerRef>(null)
  const { client } = useQsAdminApiManager()
  const queryClient = useQueryClient()
  const skipDraftPersistOnHideRef = useRef(false)
  const allowHideWhileSubmittingRef = useRef(false)
  const [popupHeight, setPopupHeight] = useState(
    JSON.parse(localStorage.getItem(`ticket-editor-popup`) ?? '{"height":700}').height,
  )

  const noteMutation = useMutation({
    mutationFn: async ({
      files,
      action,
      note,
      currentTicket,
    }: {
      files: File[] | undefined
      action: TicketActions
      note: TicketNoteViewModel | undefined | null
      currentTicket: ticket | null | undefined
    }) => {
      return upsertTicketNote(client, {
        ticketRecordId: note?.ticketRecordId ?? currentTicket?.id ?? 0,
        noteId: note?.id ?? 0,
        content: note?.testo ?? '',
        contentFormat: 'html',
        send: note?.sentCompatibility ? true : action === TicketActions.SendTicketNote,
        tag: note?.tag,
        attachments: files,
      })
    },
    onSuccess: async (_response, variables) => {
      const currentTicketRecordId = variables.note?.ticketRecordId ?? variables.currentTicket?.id
      if (currentTicketRecordId) {
        await queryClient.invalidateQueries({ queryKey: getTicketNoteQueryKey(currentTicketRecordId) })
      }
      skipDraftPersistOnHideRef.current = true
      if (variables.action === TicketActions.SendTicketNote)
        writeTicketEditorDraft('send-note', variables.note?.ticket?.id, null)
      if (variables.action === TicketActions.CreateTicketNote)
        writeTicketEditorDraft('internal-note', variables.note?.ticket?.id, null)
      notify('Nota salvata con successo', 'success', 3000)
      if (typeof window !== 'undefined' && variables.currentTicket?.id != null) {
        const channel = new BroadcastChannel(`ticket-${variables.currentTicket.id}`)
        channel.postMessage({ type: 'note-added' })
        channel.close()
      }
      allowHideWhileSubmittingRef.current = true
      refPopup.current?.instance().hide()
    },
    onError: (error: any) => {
      notify(
        `Errore durante il salvataggio della nota: ${error?.response?.data?.error?.exceptionMessage ?? error?.response?.data?.error?.message ?? error?.message ?? 'Errore sconosciuto'}`,
        'error',
        3000,
      )
    },
  })

  const completeMutation = useMutation({
    mutationFn: async (complete: { ticket: ticket | null | undefined; closure: string; solution: string }) => {
      return client.post(
        `/api/tickets/${complete.ticket?.id}/complete`,
        {
          closureContent: complete.closure,
          closureContentFormat: 'html',
          solution: complete.solution,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
    },
    onSuccess: async (_response, complete) => {
      if (complete.ticket?.id != null) {
        await queryClient.invalidateQueries({ queryKey: getTicketDetailQueryKey(complete.ticket.id) })
        await queryClient.invalidateQueries({ queryKey: getTicketNoteQueryKey(complete.ticket.id) })
      }
      skipDraftPersistOnHideRef.current = true
      writeTicketEditorDraft('closure-note', complete.ticket?.id, null)
      writeTicketEditorDraft('solution', complete.ticket?.id, null)
      notify('Ticket completato con successo', 'success', 3000)
      if (typeof window !== 'undefined' && complete.ticket?.id != null) {
        const channel = new BroadcastChannel(`ticket-${complete.ticket.id}`)
        channel.postMessage({ type: 'ticket-updated' })
        channel.close()
      }
      allowHideWhileSubmittingRef.current = true
      refPopup.current?.instance().hide()
    },
    onError: (error: any) => {
      notify(
        `Errore durante il completamento del ticket: ${error?.response?.data?.error?.exceptionMessage ?? error?.response?.data?.error?.message ?? error?.message ?? 'Errore sconosciuto'}`,
        'error',
        3000,
      )
    },
  })
  const isBlockingAction = noteMutation.isPending || completeMutation.isPending

  const renderContent = useCallback(() => {
    const editorHeight = popup.action === TicketActions.EditTicketNote ? popupHeight - 480 : popupHeight - 300

    return (
      <>
        {popup.action === TicketActions.Complete ? (
          <TicketEditorCompleteForm
            key={`complete-${popup.complete?.ticket?.id ?? 'new'}`}
            height={editorHeight}
            complete={popup.complete}
            action={popup.action}
            isSubmitting={completeMutation.isPending}
            onDraftChanged={(changes) => {
              if (changes.closure !== undefined) {
                writeTicketEditorDraft('closure-note', popup.complete?.ticket?.id, changes.closure)
              }
              if (changes.solution !== undefined) {
                writeTicketEditorDraft('solution', popup.complete?.ticket?.id, changes.solution)
              }
            }}
            onSaveClick={() => {
              if (!popup.complete) return
              completeMutation.mutate(popup.complete)
            }}
          ></TicketEditorCompleteForm>
        ) : (
          <>
            {popup.action === TicketActions.ShowTicketNote ? (
              popup.note ? (
                <div style={{ maxHeight: editorHeight, overflow: 'auto' }}>
                  <NoteView note={popup.note} readOnly={true} visibleEditClick={false} />
                </div>
              ) : null
            ) : (
              <TicketEditorNoteForm
                key={`note-${popup.action}-${popup.note?.id ?? 'new'}-${popup.note?.tag ?? 'untagged'}-${popup.ticket?.id ?? popup.note?.ticket?.id ?? 'ticket'}`}
                height={editorHeight}
                note={popup.note}
                action={popup.action}
                readOnly={
                  !!(popup.action === TicketActions.EditTicketNote && !canEditTicketNote(popup.note, userInfo?.name))
                }
                isSubmitting={noteMutation.isPending}
                onDraftChanged={(content) => {
                  if (popup.action === TicketActions.SendTicketNote) {
                    writeTicketEditorDraft('send-note', popup.note?.ticket?.id, content)
                    return
                  }
                  if (popup.action === TicketActions.CreateTicketNote) {
                    writeTicketEditorDraft('internal-note', popup.note?.ticket?.id, content)
                  }
                }}
                onSaveClick={async (files: File[] | undefined) => {
                  noteMutation.mutate({
                    files,
                    action: popup.action,
                    note: popup.note,
                    currentTicket: popup.ticket,
                  })
                }}
              ></TicketEditorNoteForm>
            )}
            {popup.action === TicketActions.ShowTicketNote && (
              <DocumentList
                title="Allegati"
                folderPath={`${popup.note?.ticket?.attachmentsFolderUrl}/_notes/${popup.note?.tag}/attachments`}
                documentListGetRef={documentListRef}
                preliminarPermissionsAction={async () => {
                  return await getTicketAttachmentPermissions(client, popup.note?.ticketRecordId, {
                    objectId: popup.note?.ticket?.id,
                    path: popup.note?.ticket?.attachmentsFolderUrl ?? '',
                  })
                }}
                readOnly={true}
              />
            )}
            {popup.action === TicketActions.EditTicketNote && (
              <AttachmentsManager
                fileManagerRef={fileManagerRef}
                folderUrl={`${popup.note?.ticket?.attachmentsFolderUrl}/_notes/${popup.note?.tag}/attachments`}
                preliminarPermissionsAction={async () => {
                  return await getTicketAttachmentPermissions(client, popup.note?.ticketRecordId, {
                    objectId: popup.note?.ticket?.id,
                    path: popup.note?.ticket?.attachmentsFolderUrl ?? '',
                  })
                }}
                createDirectoryAction={async (rootFolderPath: string, name: string, conflictBehavior: string) => {
                  return await createTicketAttachmentFolder(client, popup.note?.ticketRecordId ?? 0, {
                    rootFolder: rootFolderPath,
                    newFolderName: name,
                    conflictBehavior,
                    objectId: popup.note?.ticket?.id,
                  })
                }}
                height={250}
              />
            )}
          </>
        )}
      </>
    )
  }, [
    client,
    completeMutation.isPending,
    noteMutation.isPending,
    popup.action,
    popup.complete,
    popup.note,
    popup.ticket?.ded_Dis,
    popup.ticket?.id,
    popupHeight,
    refPopup,
    userInfo?.name,
  ])
  return (
    <>
      <Popup
        ref={refPopup}
        id="ticket_action_popup"
        hideOnOutsideClick={false}
        showCloseButton={!isBlockingAction}
        onHiding={(e) => {
          if (allowHideWhileSubmittingRef.current) {
            allowHideWhileSubmittingRef.current = false
            return
          }
          if (isBlockingAction) {
            e.cancel = true
            return
          }
          if (skipDraftPersistOnHideRef.current) {
            skipDraftPersistOnHideRef.current = false
            return
          }
          if (
            popup.action === TicketActions.SendTicketNote ||
            popup.action === TicketActions.CreateTicketNote ||
            popup.action === TicketActions.Complete
          ) {
            switch (popup.action) {
              case TicketActions.SendTicketNote:
                writeTicketEditorDraft('send-note', popup.note?.ticket?.id, popup.note?.testo)
                break
              case TicketActions.CreateTicketNote:
                writeTicketEditorDraft('internal-note', popup.note?.ticket?.id, popup.note?.testo)
                break
              case TicketActions.Complete:
                writeTicketEditorDraft('closure-note', popup.complete?.ticket?.id, popup.complete?.closure)
                writeTicketEditorDraft('solution', popup.complete?.ticket?.id, popup.complete?.solution)
                break
              default:
                break
            }
          }
        }}
        showTitle={true}
        title={TicketActionDescriptions[popup.action]}
        container=".dx-viewport"
        resizeEnabled={currentScreenSize.isLarge || currentScreenSize.isMedium ? true : false}
        defaultWidth={
          currentScreenSize.isLarge || currentScreenSize.isMedium
            ? JSON.parse(localStorage.getItem(`ticket-editor-popup`) ?? '{"width":1200}').width
            : currentScreenSize.isXSmall
              ? 350
              : 700
        }
        defaultHeight={
          currentScreenSize.isXSmall
            ? 700
            : currentScreenSize.isSmall
              ? 300
              : JSON.parse(localStorage.getItem(`ticket-editor-popup`) ?? `{"height":"auto"}`).height
        }
        onResizeEnd={(e) => {
          localStorage.setItem(`ticket-editor-popup`, JSON.stringify({ height: e.height, width: e.width }))
          setPopupHeight(e.height)
        }}
        contentRender={renderContent}
      ></Popup>
    </>
  )
}

export default TicketEditorPopup
